// Area e scala della cattura dello schermo per la segnalazione (captureScreenshot.ts).
//
// Si fotografa esattamente la finestra visibile (larghezza x altezza della finestra, a partire dallo
// scorrimento corrente), mai l'intero documento: con html2canvas il documento clonato risultava piu'
// grande della finestra e restavano fasce di solo sfondo a destra e in basso.
// La scala segue il devicePixelRatio (immagine nitida) ma la larghezza finale non supera
// MAX_CAPTURE_WIDTH. Opzioni usate di modern-screenshot: width, height, scale, style.

export interface ViewportInfo {
  innerWidth: number;
  innerHeight: number;
  scrollX: number;
  scrollY: number;
  devicePixelRatio: number;
}

export interface CaptureRegion {
  /** Larghezza della finestra in px CSS. */
  width: number;
  /** Altezza della finestra in px CSS. */
  height: number;
  /** Scorrimento orizzontale della finestra in px CSS. */
  scrollX: number;
  /** Scorrimento verticale della finestra in px CSS. */
  scrollY: number;
  /** Pixel dell'immagine per px CSS. */
  scale: number;
}

/** Larghezza massima in pixel dell'immagine salvata (leggibile senza file pesanti). */
export const MAX_CAPTURE_WIDTH = 1600;

const finitePos = (v: number, fallback: number) => (Number.isFinite(v) && v > 0 ? v : fallback);

/** Area visibile della pagina e scala per la cattura. */
export function captureRegion(v: ViewportInfo): CaptureRegion {
  const width = Math.max(1, Math.round(finitePos(v.innerWidth, 1)));
  const height = Math.max(1, Math.round(finitePos(v.innerHeight, 1)));
  const scrollX = Math.max(0, Math.round(Number.isFinite(v.scrollX) ? v.scrollX : 0));
  const scrollY = Math.max(0, Math.round(Number.isFinite(v.scrollY) ? v.scrollY : 0));
  const dpr = finitePos(v.devicePixelRatio, 1);
  const scale = Math.min(dpr, MAX_CAPTURE_WIDTH / width);
  return { width, height, scrollX, scrollY, scale };
}

/** Dimensione attesa dell'immagine (modern-screenshot: Math.floor(lato * scale)). */
export function capturedImageSize(r: CaptureRegion): { w: number; h: number } {
  return { w: Math.floor(r.width * r.scale), h: Math.floor(r.height * r.scale) };
}

/**
 * Stile da applicare alla copia del body: dimensione della finestra e, se la finestra e' scorsa,
 * contenuto spostato in su/a sinistra (con i margini, non con transform: un transform farebbe
 * scorrere anche gli elementi position:fixed, che invece restano fermi nella finestra).
 */
export function rootStyleForRegion(r: CaptureRegion): Record<string, string> {
  const style: Record<string, string> = { overflow: 'visible' };
  if (r.scrollY) style.marginTop = `${-r.scrollY}px`;
  if (r.scrollX) style.marginLeft = `${-r.scrollX}px`;
  return style;
}
